<template>

    <!-- Loader -->
    <app-loader :loading="loading || scanning" />

    <div class="py-2 px-4 sm:px-8">

        <h1 class="flex pt-3 pb-8 items-center border-b border-gray-700 mb-4 sm:pt-6">
            <div class="text-2xl font-bold text-gray-200 sm:text-3xl">{{ $t('expenses.title') }}</div>
            <span
                v-if="totalExpense > 0"
                class="ml-auto text-2xl font-bold text-red-500 px-4 py-2
                       inline-flex justify-center
                       shadow-lg ring-1 ring-inset ring-gray-900
                       rounded-md bg-gray-900/70"
            >
                {{ $t('expenses.total', { total: totalExpense }) }}
            </span>
        </h1>

        <expenses-filters
            :expanded="showFilters"
            :start-date="startDate"
            :end-date="endDate"
            :grouped-subcategories="groupedSubcategories"
            :recurring-filter="recurringFilter"
            :search-term="searchTerm"
            :show-note-column="showNoteColumn"
            :exporting="exporting"
            @toggle="toggleFilters"
            @update:date-range="updateDateRange"
            @update:subcategory="updateSubcategory"
            @update:recurring-filter="updateRecurringFilter"
            @update:search-term="updateSearchTerm"
            @update:show-note-column="updateShowNoteColumn"
            @clear-search="clearSearchTerm"
            @apply="applyFilters"
            @export="(format) => exportExpenses(appliedFilters, format)"
        />

        <!-- Table of Expenses -->
        <app-table
            :columns="expenseColumns"
            :data="filteredExpenses"
            :show-actions="true"
            actions-column-label=""
            :sortable="true"
            :sort-option="selectedSortOption"
            :sort-direction="selectedSortDirection"
            @sort-changed="handleSortChange"
            class="mt-4 sm:mt-0"
        >
            <template v-slot:actions="{ item }">
                <app-row-actions @edit="editExpense(item)" @delete="deleteExpense(item)" />
            </template>
        </app-table>

        <!-- Show message when there are no expenses -->
        <div v-if="filteredExpenses.length === 0" class="p-4 mb-8 bg-gray-700/20 text-white rounded-md text-center py-16">
            <p class="whitespace-pre-line">{{ noDataMessage ? $t(noDataMessage) : '' }}</p>
        </div>

        <!-- Pagination -->
        <app-pagination
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            :max-visible-pages="5"
            @update-current-page="handlePageChange"
        />

        <!-- Extra space so the FAB button doesn't cover the edit/delete buttons when there is no pagination -->
        <div class="h-20"></div>

        <app-fab-menu
            @add="triggerManualAdd"
            @scanned="onReceiptScanned"
            @update:scanning="scanning = $event"
        />

        <add-modal
            :is-add-modal-open="isAddModalOpen"
            :prefill="receiptPrefill"
            @update:is-add-modal-open="isAddModalOpen = $event"
            @expense-actions="fetchExpensesData"
        ></add-modal>

        <edit-modal
            :is-edit-modal-open="isEditModalOpen"
            :selected-expense="selectedExpense"
            @update:is-edit-modal-open="isEditModalOpen = $event"
            @expense-actions="fetchExpensesData"
            @expense-edited="fetchExpensesData"
        ></edit-modal>

    </div>

</template>

<script>
import { ref } from 'vue';
import { useExpensesList } from '@/composables/useExpensesList';
import { useExpensesExport } from '@/composables/useExpensesExport';
import AppLoader from "@/components/common/AppLoader.vue";
import AppTable from "@/components/common/AppTable";
import AppPagination from '@/components/common/AppPagination';
import AppRowActions from "@/components/common/AppRowActions.vue";
import AppFabMenu from "@/components/common/AppFabMenu.vue";
import ExpensesFilters from "@/components/expenses/ExpensesFilters.vue";
import AddModal from "@/components/expenses/AddModal";
import EditModal from "@/components/expenses/EditModal";

export default {
    components: {
        AppLoader,
        AppTable,
        AppPagination,
        AppRowActions,
        AppFabMenu,
        ExpensesFilters,
        AddModal,
        EditModal
    },
    setup() {
        const expensesList = useExpensesList();
        const scanning = ref(false);

        // Blank by default; replaced with the scan's best-effort reading right before the modal
        // opens (a *new* object, so FormModal's values watcher refills it)
        const receiptPrefill = ref({});

        const triggerManualAdd = () => {
            receiptPrefill.value = {};
            expensesList.openAddModal();
        };

        const onReceiptScanned = (prefill) => {
            receiptPrefill.value = prefill;
            expensesList.openAddModal();
        };

        return {
            ...expensesList,
            ...useExpensesExport(),
            scanning,
            receiptPrefill,
            triggerManualAdd,
            onReceiptScanned,
        };
    }
}
</script>

<style>
.rec-icon {
    font-size: 1rem;
    color: rgba(239, 68, 68, 1);
}
</style>
